import React, { useState, useEffect } from 'react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Shield, Thermometer, Brain, Settings2, Save, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { adminSettingsService, DEFAULT_ADMIN_SETTINGS } from '@/services/adminSettingsService';

interface AdminSettings {
  llm_temperature: number;
  enable_model_fallback: boolean;
  maintenance_mode: boolean;
}

export default function AdminTab() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<AdminSettings>({
    llm_temperature: 0.2,
    enable_model_fallback: true,
    maintenance_mode: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadAdminSettings();
  }, []);

  const loadAdminSettings = async () => {
    try {
      setIsLoading(true);
      const currentSettings = await adminSettingsService.getSettings();
      setSettings(currentSettings);
    } catch (error: any) {
      console.error('Error loading admin settings:', error);
      toast({
        title: "Error",
        description: "Failed to load admin settings. Using defaults.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveAdminSettings = async () => {
    try {
      setIsSaving(true);

      // Prepare settings for upsert
      const settingsToSave = [
        {
          key: 'llm_temperature',
          value: settings.llm_temperature.toString(),
          category: 'llm',
          description: 'Controls creativity vs determinism in AI responses',
          is_enabled: true,
        },
        {
          key: 'enable_model_fallback',
          value: null,
          category: 'llm',
          description: 'Enable fallback to secondary models when primary fails',
          is_enabled: settings.enable_model_fallback,
        },
        {
          key: 'maintenance_mode',
          value: null,
          category: 'system',
          description: 'Enable maintenance mode to restrict user access',
          is_enabled: settings.maintenance_mode,
        },
      ];

      // Upsert each setting
      for (const setting of settingsToSave) {
        const { error } = await supabase
          .from('settings')
          .upsert(
            {
              ...setting,
              last_updated: new Date().toISOString(),
            },
            {
              onConflict: 'key',
            }
          );

        if (error) throw error;
      }

      setHasChanges(false);
      toast({
        title: "Settings Saved",
        description: "Admin settings have been updated successfully. Changes will take effect immediately.",
      });
    } catch (error: any) {
      console.error('Error saving admin settings:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save admin settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      llm_temperature: 0.2,
      enable_model_fallback: true,
      maintenance_mode: false,
    });
    setHasChanges(true);
  };

  const updateSetting = <K extends keyof AdminSettings>(key: K, value: AdminSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    
    // Update the service immediately for real-time effect
    adminSettingsService.updateSettings({ [key]: value });
  };

  const getTemperatureDescription = (temp: number) => {
    if (temp <= 0.1) return "Very Conservative - Highly predictable, professional responses";
    if (temp <= 0.3) return "Conservative - Balanced and reliable responses";
    if (temp <= 0.5) return "Moderate - Some creativity while maintaining professionalism";
    if (temp <= 0.7) return "Creative - More varied and expressive responses";
    return "Highly Creative - Very diverse but potentially unpredictable responses";
  };

  const getTemperatureColor = (temp: number) => {
    if (temp <= 0.1) return "text-blue-400";
    if (temp <= 0.3) return "text-green-400";
    if (temp <= 0.5) return "text-yellow-400";
    if (temp <= 0.7) return "text-orange-400";
    return "text-red-400";
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="space-y-4">
          <div className="h-8 bg-stewie-gray/20 rounded animate-pulse"></div>
          <div className="h-32 bg-stewie-gray/20 rounded animate-pulse"></div>
          <div className="h-32 bg-stewie-gray/20 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-2 flex items-center gap-2">
          <Shield className="h-6 w-6 text-red-400" />
          Administrator Settings
        </h2>
        <p className="text-stewie-gray">
          Configure system-wide settings and AI behavior. Changes take effect immediately.
        </p>
      </div>

      {/* LLM Temperature Control */}
      <Card className="bg-stewie-charcoal border-stewie-gray/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Thermometer className="h-5 w-5 text-blue-400" />
            LLM Temperature Control
          </CardTitle>
          <CardDescription className="text-stewie-gray">
            Control the creativity vs determinism of AI responses across the entire application.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-white">Temperature</Label>
              <span className={`text-lg font-mono px-3 py-1 rounded ${getTemperatureColor(settings.llm_temperature)} bg-stewie-dark`}>
                {settings.llm_temperature.toFixed(2)}
              </span>
            </div>
            
            <div className="space-y-2">
              <Slider
                value={[settings.llm_temperature]}
                onValueChange={(value) => updateSetting('llm_temperature', value[0])}
                max={1.0}
                min={0.0}
                step={0.05}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-stewie-gray">
                <span>0.0 - Deterministic</span>
                <span>0.5 - Balanced</span>
                <span>1.0 - Creative</span>
              </div>
            </div>
            
            <div className={`p-3 rounded border-l-4 bg-stewie-dark/50 ${
              settings.llm_temperature <= 0.3 
                ? 'border-l-green-400 bg-green-900/20' 
                : settings.llm_temperature <= 0.7 
                  ? 'border-l-yellow-400 bg-yellow-900/20'
                  : 'border-l-red-400 bg-red-900/20'
            }`}>
              <p className={`text-sm ${getTemperatureColor(settings.llm_temperature)}`}>
                {getTemperatureDescription(settings.llm_temperature)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card className="bg-stewie-charcoal border-stewie-gray/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-stewie-blue" />
            System Configuration
          </CardTitle>
          <CardDescription className="text-stewie-gray">
            Control system-wide behavior and failover settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-white">Model Fallback</Label>
              <p className="text-xs text-stewie-gray">Enable backup models when primary LLM fails</p>
            </div>
            <Switch
              checked={settings.enable_model_fallback}
              onCheckedChange={(checked) => updateSetting('enable_model_fallback', checked)}
              className="data-[state=checked]:bg-stewie-blue"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-white">Maintenance Mode</Label>
              <p className="text-xs text-stewie-gray">Restrict access for system maintenance</p>
            </div>
            <Switch
              checked={settings.maintenance_mode}
              onCheckedChange={(checked) => updateSetting('maintenance_mode', checked)}
              className="data-[state=checked]:bg-red-500"
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        <Button 
          onClick={resetToDefaults}
          variant="outline"
          className="border-stewie-gray/30 text-stewie-gray hover:bg-stewie-charcoal"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button 
          onClick={saveAdminSettings}
          disabled={!hasChanges || isSaving}
          className="bg-stewie-blue hover:bg-stewie-blue/80 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
} 